import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Store, AlertTriangle, Loader2, ShieldCheck, MapPin, Images } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr, peso } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { NeedTenant } from "../components/layout/AppShell";
import GalleryManager from "../components/GalleryManager";

export default function SettingsPage() {
  const { user, tenant, subscription, activeTenantId, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [confirmName, setConfirmName] = useState("");
  const [busy, setBusy] = useState(false);
  const [lat, setLat] = useState(tenant?.latitude != null ? String(tenant.latitude) : "");
  const [lng, setLng] = useState(tenant?.longitude != null ? String(tenant.longitude) : "");
  const [geoBusy, setGeoBusy] = useState(false);

  useEffect(() => {
    setLat(tenant?.latitude != null ? String(tenant.latitude) : "");
    setLng(tenant?.longitude != null ? String(tenant.longitude) : "");
  }, [tenant?.latitude, tenant?.longitude]);

  if (user.role === "system_admin" && !activeTenantId) return <NeedTenant />;
  if (!tenant) return null;

  const canReset = ["owner", "system_admin"].includes(user.role);
  const canEditLocation = ["owner", "manager", "system_admin"].includes(user.role);
  const match = confirmName.trim().toLowerCase() === tenant.store_name.trim().toLowerCase();

  const saveLocation = async () => {
    const la = parseFloat(lat), ln = parseFloat(lng);
    if (isNaN(la) || isNaN(ln) || la < -90 || la > 90 || ln < -180 || ln > 180) {
      toast.error("Enter valid coordinates — latitude -90 to 90, longitude -180 to 180");
      return;
    }
    setGeoBusy(true);
    try {
      await api.put("/tenant/location", { latitude: la, longitude: ln });
      toast.success("Store location saved — customers can now find you with 'Near me'");
      await checkAuth();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setGeoBusy(false); }
  };

  const detectLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation is not supported by this browser"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setLat(pos.coords.latitude.toFixed(6)); setLng(pos.coords.longitude.toFixed(6)); toast.success("Coordinates detected — click Save location"); },
      () => toast.error("Location permission denied — enter coordinates manually"),
      { timeout: 10000 }
    );
  };

  const reset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tenant/reset-data", { confirm_name: confirmName });
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Factory reset complete — ${total} records deleted`);
      setConfirmName("");
      await checkAuth();
      navigate("/dashboard");
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Settings</h1>
        <p className="text-sm text-slate-500">Store configuration and data management</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-3">
        <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
          <Store className="h-4 w-4 text-slate-500" /> Store Information
        </h3>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <p className="text-slate-500">Store</p><p className="font-medium text-slate-900">{tenant.store_name}</p>
          <p className="text-slate-500">Type</p><p className="font-medium text-slate-900 capitalize">{tenant.business_type}</p>
          <p className="text-slate-500">Status</p><p className="font-medium text-slate-900 capitalize">{tenant.status || "active"}</p>
          <p className="text-slate-500">Plan</p><p className="font-medium text-slate-900">{subscription?.plan_name || "FREE"} {subscription ? `· ${peso(subscription.price_monthly)}/mo` : ""}</p>
          <p className="text-slate-500">BIR mode</p><p className="font-medium text-slate-900">{tenant.bir_compliance_enabled ? "Enabled" : "Disabled"}</p>
          <p className="text-slate-500">Multi-branch</p><p className="font-medium text-slate-900">{tenant.multi_branch_enabled ? "Enabled" : "Disabled"}</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4" data-testid="store-location-card">
        <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
          <MapPin className="h-4 w-4 text-slate-500" /> Store Location
        </h3>
        <p className="text-sm text-slate-500">
          Pin your store coordinates so public portal customers can discover {tenant.store_name} with the "Near me" search, sorted by distance.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Latitude</Label>
            <Input value={lat} onChange={(e) => setLat(e.target.value)} placeholder="e.g. 14.5995" className="font-mono" disabled={!canEditLocation} data-testid="store-latitude-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Longitude</Label>
            <Input value={lng} onChange={(e) => setLng(e.target.value)} placeholder="e.g. 120.9842" className="font-mono" disabled={!canEditLocation} data-testid="store-longitude-input" />
          </div>
        </div>
        {canEditLocation && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={detectLocation} data-testid="detect-location-btn">Use my current location</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={saveLocation} disabled={geoBusy || !lat || !lng} data-testid="save-location-btn">
              {geoBusy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Save location
            </Button>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4" data-testid="store-gallery-card">
        <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
          <Images className="h-4 w-4 text-slate-500" /> Photo Gallery &amp; Promotions
        </h3>
        <p className="text-sm text-slate-500">
          Upload images from your device. Feature photos showcase {tenant.store_name} on its public portal page;
          promo banners appear on the portal store page and as a slim banner inside your staff workspace.
          Max 10 images per type, 5 MB each (JPG, PNG, WEBP, GIF).
        </p>
        {canEditLocation ? (
          <GalleryManager testid="store-gallery" />
        ) : (
          <p className="text-xs text-slate-400">Only the store owner, manager or a platform admin can manage gallery images.</p>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-3">
        <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-slate-500" /> Data Retention
        </h3>
        <p className="text-sm text-slate-500">
          The audit trail is a compliance ledger and is never deleted. Audit records survive factory resets.
        </p>
      </div>

      <div className="bg-red-50 border border-red-200 rounded-xl p-6 space-y-4" data-testid="danger-zone">
        <h3 className="font-heading font-semibold text-red-900 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> Danger Zone — Factory Reset
        </h3>
        <p className="text-sm text-red-800 leading-relaxed">
          This permanently deletes <span className="font-semibold">all operational data</span> for {tenant.store_name}:
          products, invoices/sales, attendance records, clients, expenses, portal orders, payroll payouts,
          BIR profile, tax configuration, and invoice series. User accounts, branches and store settings are kept.
          This cannot be undone.
        </p>
        {canReset ? (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-red-900">Type the store name (<span className="font-mono font-semibold">{tenant.store_name}</span>) to confirm</Label>
              <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)}
                placeholder={tenant.store_name} className="bg-white border-red-200" data-testid="reset-confirm-input" />
            </div>
            <Button className="bg-red-600 hover:bg-red-700" disabled={!match || busy} onClick={reset} data-testid="reset-store-data-btn">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Reset all store data to factory defaults
            </Button>
          </div>
        ) : (
          <p className="text-xs text-red-700">Only the store owner or a platform admin can perform a factory reset.</p>
        )}
      </div>
    </div>
  );
}
